/**
 * Ngày tháng theo cách dữ liệu đang lưu: ngày đăng video là «DD/MM» + năm tách riêng, mốc tuần
 * là «DD/MM/YYYY» của ngày CHỦ NHẬT. Mọi phép cộng trừ ngày dùng `Date` THẬT có năm — phép cộng
 * trên chuỗi «DD/MM» từng quay vòng trong một năm, cuối tháng 12 ra tháng 1 CỦA CÙNG NĂM.
 *
 * TUẦN CHẠY TỪ CHỦ NHẬT TỚI THỨ 7 (28/09/2026, theo yêu cầu chủ dự án: «chủ nhật tuần trước đến
 * thứ 7 tuần này»). Trước đó tuần chạy Thứ 2 → Chủ nhật. Đây là MỘT chỗ duy nhất định nghĩa mốc
 * tuần cho cả app — Thị trường, KPI tuần, Họp tuần, Update thumbnail, Producer, SEO tuần, Dự án
 * đều đi qua đây, nên đổi ở đây là đổi hết, không nơi nào tự tính riêng.
 */

/**
 * Nhãn thứ tra theo `weekdayIndex` — ĐÂY LÀ THỨ TỰ LƯU TRỮ, KHÔNG PHẢI THỨ TỰ HIỂN THỊ.
 *
 * «Lịch chạy» lưu các ngày trong tuần bằng chính chỉ số này (0 = Thứ 2). Đảo mảng cho Chủ nhật lên
 * đầu là mọi lịch đã khai bị lệch một ngày, im lặng. Mốc tuần đổi sang Chủ nhật không kéo theo
 * chuyện này: một bên là «tuần gồm bảy ngày nào», bên kia là «ngày này là thứ mấy».
 */
export const WEEKDAY_LABELS = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'] as const;

const pad2 = (value: number) => String(value).padStart(2, '0');

export interface CalendarDate {
  day: number;
  month: number;
  year: number;
}

export function isValidCalendarDate({ day, month, year }: CalendarDate): boolean {
  if (!Number.isInteger(day) || !Number.isInteger(month) || !Number.isInteger(year)) return false;
  if (month < 1 || month > 12 || day < 1) return false;
  // Năm nhuận tính theo CHÍNH NĂM ĐƯỢC NHẬP (bản cũ tính theo năm hiện tại → 29/02 sai).
  return day <= new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** «5/8», «05/08» → {day, month}. Sai dạng → null. */
export function parseDayMonth(
  text: string | null | undefined,
): { day: number; month: number } | null {
  const match = /^\s*(\d{1,2})\/(\d{1,2})\s*$/.exec(text ?? '');
  if (!match) return null;
  const day = Number(match[1]);
  const month = Number(match[2]);
  return month >= 1 && month <= 12 && day >= 1 && day <= 31 ? { day, month } : null;
}

/** «DD/MM/YYYY» → CalendarDate. Sai dạng hoặc ngày không có thật → null. */
export function parseFullDate(text: string | null | undefined): CalendarDate | null {
  const match = /^\s*(\d{1,2})\/(\d{1,2})\/(\d{4})\s*$/.exec(text ?? '');
  if (!match) return null;
  const date = { day: Number(match[1]), month: Number(match[2]), year: Number(match[3]) };
  return isValidCalendarDate(date) ? date : null;
}

export function formatDayMonth({ day, month }: { day: number; month: number }): string {
  return `${pad2(day)}/${pad2(month)}`;
}

export function formatFullDate(date: CalendarDate): string {
  return `${formatDayMonth(date)}/${date.year}`;
}

/** Khoá tháng «MM/YYYY». */
export function monthKeyOf(date: CalendarDate): string {
  return `${pad2(date.month)}/${date.year}`;
}

/** «08/2026» → «tháng 8/2026». */
export function monthLabel(monthKey: string): string {
  const [month, year] = monthKey.split('/');
  return `tháng ${Number(month)}/${year}`;
}

const toUtc = ({ day, month, year }: CalendarDate) => new Date(Date.UTC(year, month - 1, day));
const fromUtc = (date: Date): CalendarDate => ({
  day: date.getUTCDate(),
  month: date.getUTCMonth() + 1,
  year: date.getUTCFullYear(),
});

export function addDays(date: CalendarDate, days: number): CalendarDate {
  const next = toUtc(date);
  next.setUTCDate(next.getUTCDate() + days);
  return fromUtc(next);
}

/** 0 = Thứ 2 … 6 = Chủ nhật. Chỉ để TRA NHÃN THỨ và lưu «Lịch chạy» — xem `WEEKDAY_LABELS`. */
export function weekdayIndex(date: CalendarDate): number {
  return (toUtc(date).getUTCDay() + 6) % 7;
}

/** Chủ nhật mở đầu tuần chứa `date`. Chính `date` là Chủ nhật thì trả lại nó. */
export function weekStartOf(date: CalendarDate): CalendarDate {
  // `getUTCDay` đếm 0 = Chủ nhật, đúng ngay mốc tuần đang dùng nên không phải xoay vòng.
  return addDays(date, -toUtc(date).getUTCDay());
}

/** Nhãn tuần «DD/MM–DD/MM», từ Chủ nhật tới Thứ 7. */
export function weekRangeLabel(weekStart: CalendarDate): string {
  return `${formatDayMonth(weekStart)}–${formatDayMonth(addDays(weekStart, 6))}`;
}

/**
 * Số so sánh được: lớn hơn = muộn hơn. CHỈ để so sánh / sắp xếp — hiệu hai số KHÔNG phải số ngày
 * (30/10 − 22/09 ra 108); đếm ngày thì dùng `daysBetween`.
 */
export function dateOrdinal({ day, month, year }: CalendarDate): number {
  return year * 10_000 + month * 100 + day;
}

/** Số ngày lịch từ `from` tới `to` (âm khi `to` trước `from`). */
export function daysBetween(from: CalendarDate, to: CalendarDate): number {
  return Math.round((toUtc(to).getTime() - toUtc(from).getTime()) / 86_400_000);
}

/** «Hôm nay» theo đồng hồ MÁY NGƯỜI DÙNG — đúng như bản cũ (người dùng đều ở Việt Nam). */
export function today(now: Date = new Date()): CalendarDate {
  return { day: now.getDate(), month: now.getMonth() + 1, year: now.getFullYear() };
}

export interface WeekColumn {
  /** Khoá lưu trữ: «DD/MM/YYYY» của ngày CHỦ NHẬT mở đầu tuần. */
  key: string;
  /** «T1»…«Tn», cũ nhất trước. */
  shortLabel: string;
  rangeLabel: string;
  isCurrent: boolean;
}

/**
 * Trục tuần của bảng Thị trường: `count` tuần gần nhất tính từ hôm nay, cũ nhất trước. Số liệu
 * lưu theo MỐC TUẦN nên sang tuần mới cột tự dịch sang trái, số cũ vẫn dính đúng tuần của nó.
 */
export function recentWeeks(count: number, reference: CalendarDate = today()): WeekColumn[] {
  const thisWeek = weekStartOf(reference);
  return Array.from({ length: count }, (_, index) => {
    const weekStart = addDays(thisWeek, -7 * (count - 1 - index));
    return {
      key: formatFullDate(weekStart),
      shortLabel: `T${index + 1}`,
      rangeLabel: weekRangeLabel(weekStart),
      isCurrent: index === count - 1,
    };
  });
}
